import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { IPressureDropCalculator, IPressureDropResponse, unitId } from "../../interfaces/interfaces";
import { apiSlice } from "./apiSlice";
import { unitConverter } from "../../components/MeasurementUnits/UnitConverter";


const emptyValues: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
const initialState: IPressureDropCalculator = {
	inputs: [
		{
			id: 'pressure-drop-pressure-downstream',
			type: 'string',
			label: '',
			queryName: 'pressure_downstream',
			baseUnitId: 'bar',
			baseValue: 80,
			currentValue: 80,
			currentUnitId: 'bar',
			unitCategoryId: 'pressure',
			userValue: null,
			userUnitId: null,
			// min: 1.01325,
			// max: 500
		},
		{
			id: 'pressure-drop-temperature-upstream',
			type: 'string',
			label: '',
			queryName: 'temperature_upstream',
			baseUnitId: 'celsius',
			baseValue: 15,
			currentValue: 15,
			currentUnitId: 'celsius',
			unitCategoryId: 'temperature',
			userValue: null,
			userUnitId: null,
		},
		{
			id: 'pressure-drop-temperature-downstream',
			type: 'string',
			label: '',
			queryName: 'temperature_downstream',
			baseUnitId: 'celsius',
			baseValue: 10,
			currentValue: 10,
			currentUnitId: 'celsius',
			unitCategoryId: 'temperature',
			userValue: null,
			userUnitId: null,
		},
		{
			id: 'pressure-drop-gas-rate-max',
			type: 'string',
			label: '',
			queryName: 'gas_rate_max',
			baseUnitId: 'mln_m3_day',
			baseValue: 2,
			currentValue: 2,
			currentUnitId: 'mln_m3_day',
			unitCategoryId: 'q',
			userValue: null,
			userUnitId: null,
		},
		{
			id: 'pressure-drop-pipeline-id',
			type: 'string',
			label: '',
			queryName: 'pipeline_id',
			baseUnitId: 'meter',
			baseValue: 0.1,
			currentValue: 0.1,
			currentUnitId: 'meter',
			unitCategoryId: 'length',
			userValue: null,
			userUnitId: null,
			unitList: ["meter", "kilometer"],
		},
		{
			id: 'pressure-drop-length',
			type: 'string',
			label: '',
			queryName: 'l',
			baseUnitId: 'kilometer',
			baseValue: 10,
			currentValue: 10,
			currentUnitId: 'kilometer',
			unitCategoryId: 'length',
			userValue: null,
			userUnitId: null,
			unitList: ["kilometer", "meter"]
		},
		{
			id: 'pressure-drop-roughness',
			type: 'string',
			label: '',
			queryName: 'roughness',
			baseUnitId: 'millimeter',
			baseValue: 0.03,
			currentValue: 0.03,
			currentUnitId: 'millimeter',
			unitCategoryId: 'length',
			userValue: null,
			userUnitId: null,
			unitList: ["millimeter"]
		},
		{
			id: 'pressure-drop-mult-friction',
			type: 'string',
			label: '',
			queryName: 'mult_friction',
			baseUnitId: null,
			baseValue: 1,
			currentValue: 1,
			currentUnitId: null,
			unitCategoryId: null,
			userValue: null,
			userUnitId: null,
		},
	],
	results: {
		gasRate: emptyValues,
		pressureWh: emptyValues,
		pressureDrop: emptyValues,
		pressureGradient: emptyValues,
		maximumVelocity: emptyValues
	},
	downloadLink: "",
	chartConfig: {},
	requiredSubscriptionLevel: "free",
	chartsData: {
		p: [],
		q: [],
		l: []
	}
};


const pressureDropCalculatorSlice = createSlice({
	name: "pressureDropCalculatorSlice",
	initialState,
	selectors: {
	},
	reducers: {
		setInputValue: (state, action: PayloadAction<{ inputName: string, value: number | null }>) => {
			state.inputs.forEach(i => {
				if (i.queryName === action.payload.inputName) {
					i.currentValue = action.payload.value;
					i.baseValue = (i.unitCategoryId && action.payload.value != null) ? unitConverter.convert(action.payload.value, i.currentUnitId, i.baseUnitId) : action.payload.value;
					i.userValue = action.payload.value;
					i.userUnitId = i.currentUnitId;
				}
			});

			state.results = {
				gasRate: emptyValues,
				pressureWh: emptyValues,
				pressureDrop: emptyValues,
				pressureGradient: emptyValues,
				maximumVelocity: emptyValues
			};

			state.chartsData = {
				p: [],
				q: [],
				l: []
			};
		},
		setInputUnit: (state, action: PayloadAction<{ inputId: string, toUnitId: unitId | null }>) => {
			state.inputs.forEach(i => {
				if (i.id === action.payload.inputId) {
					i.currentValue = i.currentValue != null ? unitConverter.convert(i.currentValue, i.currentUnitId, action.payload.toUnitId) : null;
					i.currentUnitId = action.payload.toUnitId;
					i.baseValue = i.userValue && i.userUnitId
						? unitConverter.convert(i.userValue, i.userUnitId, i.baseUnitId)
						: unitConverter.convert(i.currentValue ?? 0, i.currentUnitId, i.baseUnitId);

					if (i.errorParams?.["min"] && i.min) {
						i.errorParams = { min: unitConverter.convert(i.min, i.baseUnitId, i.currentUnitId) };
					}
					if (i.errorParams?.["max"] && i.max) {
						i.errorParams = { max: unitConverter.convert(i.max, i.baseUnitId, i.currentUnitId) };
					}
				}
			})
		},
		validateInputs: (state) => {
			state.inputs.forEach(input => {
				if (input.min != null && input.currentValue != null && input.currentValue < input.min) {
					input.error = "validation.min_value_error";
					input.errorParams = { min: input.min };
				} else if (input.max != null && input.currentValue != null && input.currentValue > input.max) {
					input.error = "validation.max_value_error";
					input.errorParams = { max: input.max };
				} else {
					input.error = null;
					input.errorParams = null;
				}
			});
		},
		insertValuesInARow: (state, action: PayloadAction<{ values: number[], startIndex: number }>) => {
			const { values, startIndex } = action.payload;

			state.inputs = state.inputs.map((input, index) => {
				const value = values[index - startIndex];
				if (value === undefined) {
					return input;
				}

				return {
					...input,
					currentValue: value,
					baseValue: input.unitCategoryId
						? unitConverter.convert(value, input.currentUnitId, input.baseUnitId)
						: value,
					userValue: value,
					userUnitId: input.currentUnitId,
				};
			})
		}
	},
	extraReducers: (builder) => {
		builder
			.addMatcher(apiSlice.endpoints.calculator.matchFulfilled, (state, action) => {
				const params = action.meta.arg.originalArgs as Record<string, string>;
				if (!params?.calculator_type) {
					console.warn("No calculator_type in calculator response");
					return;
				}
				if (params.calculator_type != "pressure_drop") {
					return;
				}

				const response = action.payload as IPressureDropResponse;
				if (response?.results) {
					state.results.gasRate = [...response.results.gas_rate];
					state.results.pressureWh = [...response.results.pressure_wh];
					state.results.pressureDrop = [...response.results.pressure_drop];
					state.results.pressureGradient = [...response.results.pressure_gradient];
					state.results.maximumVelocity = [...response.results.maximum_velocity];

					state.chartsData.p = [...response.results.pressure_wh];
					state.chartsData.q = [...response.results.gas_rate];
					state.chartsData.l = [...response.results.pressure_gradient];
				}
			})
	}
})

export default pressureDropCalculatorSlice

export const {
	setInputValue,
	setInputUnit,
	validateInputs,
	insertValuesInARow
} = pressureDropCalculatorSlice.actions